import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";

const normalizeRole = (role) => {
  const raw = typeof role === "string" ? role : role?.name || role?.authority;
  if (!raw || typeof raw !== "string") return "";
  return raw.toUpperCase().replace(/^ROLE_/, "");
};

const apprenticeVideos = [
  {
    title: "01 Noten",
    fileName: "01_Noten.mp4",
    description: "Grade workflows, expectations and basic handling.",
  },
  {
    title: "02 Semestergespräch",
    fileName: "02_Semestergespräch.mp4",
    description: "Preparation and structure of the semester conversation.",
  },
  {
    title: "03 Lerndokumentation",
    fileName: "03_Lerndokumentation.mp4",
    description: "How to maintain and submit the learning documentation.",
  },
];

const adminVideos = [
  ...apprenticeVideos,
  {
    title: "04 Simple View",
    fileName: "04_Simple View.mp4",
    description: "A simplified overview for fast process navigation.",
  },
  {
    title: "07 Revisor",
    fileName: "07_Revisor.mp4",
    description: "Review workflow and supporting control steps.",
  },
];

const revisorVideos = [
  {
    title: "07 Revisor",
    fileName: "07_Revisor.mp4",
    description: "Review workflow and supporting control steps.",
  },
];

const videoPath = (fileName) => encodeURI(`/video-guide/${fileName}`);

function VideoCard({ video, index, audienceLabel }) {
  const source = videoPath(video.fileName);

  return (
    <article className="card-brand overflow-hidden bg-white border border-pale-dawn hover:shadow-brand-lg transition-all duration-200">
      <div className="p-4 border-b border-pale-dawn bg-pale-dawn/30">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
              <i className="fas fa-circle-play text-lg"></i>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-sunrise-red">
                  Video {index + 1}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white border border-pale-dawn text-[11px] font-medium text-gray-500">
                  {audienceLabel}
                </span>
              </div>

              <h2 className="text-base font-bold text-grey-dusk truncate">
                {video.title}
              </h2>
              <p className="text-sm text-slate-cloud mt-1 line-clamp-2">
                {video.description}
              </p>
            </div>
          </div>

          <a
            href={source}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 rounded-lg bg-white border border-pale-dawn text-gray-500 hover:text-sunrise-red hover:border-sunrise-red/30 flex items-center justify-center transition-colors shrink-0"
            title="Open video"
          >
            <i className="fas fa-up-right-from-square text-sm"></i>
          </a>
        </div>
      </div>

      <div className="p-4">
        <div className="overflow-hidden rounded-xl border border-pale-dawn bg-black shadow-sm">
          <video
            controls
            preload="metadata"
            className="block w-full aspect-video bg-black"
            aria-label={video.title}
          >
            <source src={source} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <a
            href={source}
            download
            className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-sunrise-red text-white text-xs font-semibold hover:bg-sunrise-600 transition-colors shrink-0"
          >
            <i className="fas fa-download mr-2"></i>
            Download
          </a>
        </div>
      </div>
    </article>
  );
}

function VideoLibraryPage() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Loading video library..." />;
  }

  const isAdmin = user?.roles?.some((role) => normalizeRole(role) === "ADMIN");
  const isBerufsbildner = user?.roles?.some(
    (role) => normalizeRole(role) === "BERUFSBILDNER",
  );
  const isPracticeInstructor = user?.roles?.some((role) => {
    const normalizedRole = normalizeRole(role);
    return (
      normalizedRole === "PRACTICE_INSTRUCTOR" ||
      normalizedRole === "PRAXISBILDNER"
    );
  });
  const isRevisor = user?.roles?.some(
    (role) => normalizeRole(role) === "REVISOR",
  );

  const showFullLibrary = isAdmin || isBerufsbildner || isPracticeInstructor;
  const videos = isRevisor
    ? revisorVideos
    : showFullLibrary
      ? adminVideos
      : apprenticeVideos;

  const title = isRevisor
    ? "Revisor video"
    : showFullLibrary
    ? "Complete video library"
    : "Apprentice video guide";

  const intro = isRevisor
    ? "This area contains the Revisor guide only."
    : showFullLibrary
    ? "Access all guide videos for learner support, reviews and administration."
    : "Watch the core guides for grades, semester conversations and learning documentation.";

  const audienceLabel = isRevisor
    ? "Revisor only"
    : showFullLibrary
      ? "Full access"
      : "Apprentice guide";

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <section className="card-brand-elevated p-6 bg-pale-dawn/30 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                <i className="fas fa-video text-sunrise-red mr-4"></i>
                {title}
              </h1>
              <p className="text-slate-cloud">{intro}</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-pale-dawn text-sm font-semibold text-grey-dusk">
                <i className="fas fa-film text-sunrise-red mr-2"></i>
                {videos.length} videos
              </span>

              <span className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-sunrise-red/20 text-sm font-semibold text-sunrise-red">
                <i className="fas fa-shield-alt mr-2"></i>
                Protected area
              </span>
            </div>
          </div>
        </section>

        {/* Video grid */}
        <section className="card-brand bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-pale-dawn bg-pale-dawn/30">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-play text-sunrise-red mr-3"></i>
                  Available videos
                </h2>
                <p className="text-sm text-slate-cloud mt-1">
                  Select a video to watch it directly in Journey.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-5">
            {videos.map((video, index) => (
              <VideoCard
                key={video.fileName}
                video={video}
                index={index}
                audienceLabel={audienceLabel}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default VideoLibraryPage;